<template>
  <div>
    <v-skeleton-loader
      v-if="loading"
      class="m-auto"
      type="table"
      transition="scale-transition"
    />
    <TableRemote
      v-else-if="hasPermission('sites.products.view')"
      :iconHeader="hasPermission('sites.products.add') ? 'add' : null"
      :title="$t('products.pageName')"
      :isSelectable="hasPermission('sites.products.edit')"
      :items="products"
      :pagination="pagination"
      :headers="tableHeaders"
      :itemActions="getItemActions"
      :isCustomIcon="true"
      :isInfinitScroll="true"
      :centerColumns="['inInventoryActivity', 'sds', 'report']"
      :sortableColumns="['name', 'code']"
      :notSortableColumns="['inInventoryActivity', 'sds', 'report']"
      showExpand
      hideDefaultFooter
      @sendQueryParams="fetchQuery"
      @iconAction="onClickAction"
    >
      <template #item.inInventoryActivity="{item}">
        <VCheckbox
          :value="inventoryActivity[item.id]"
          class="m-0 p-0 d-flex justify-center"
          @input="(value)=>onChangeInInventoryActivity(value,item)"
        />
      </template>
      <template #item.sds="{ item }">
        <TableFiles :files="item.sds" />
      </template>
      <template #item.report="{ item }">
        <TableFiles :files="item.report" />
      </template>
      <template #item.procedure="{ item }">
        <TableFiles :files="item.procedure" />
      </template>
      <template #expanded-item="props">
        <td
          :colspan="props.headers.length"
          class="product-inventory-table"
        >
          <Inventory
            :site="site"
            :product="props.item"
            :refreshInventory="refreshInventory"
          />
        </td>
      </template>
      <template #withInfiniteScroll>
        <div v-intersect="endIntersect" />
      </template>
    </TableRemote>
    <NoPermissionAlert
      v-else
      :message="$t('permissions.error')"
    />
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';
import { isEmpty, isPlainObject } from 'lodash';
import prettyBytes from 'pretty-bytes';
import TableFiles from '@/components/shared/content-views/slots/TableFiles';
import NoPermissionAlert from '@/components/shared/NoPermissionAlert';
import ConfirmForm from '@/components/shared/widgets/ConfirmForm';
import Inventory from '@/views/associatedProducts/Inventory.vue';
import InventoryForm from '@/components/forms/InventoryForm';
import TableRemote from '@/components/shared/content-views/VTableRemote';
import perpage from '@/constants/pagination';
import AssociateProductForm from './AssociateProductForm.vue';
import VCheckbox from '../../components/shared/widgets/forms/VuetifyInputs/VCheckbox.vue';

const FILE_FIELDNAMES = ['sds', 'report', 'procedure'];

export default {
    components: {
        TableRemote,
        TableFiles,
        NoPermissionAlert,
        Inventory,
        VCheckbox,
    },
    data() {
        return {
            loading: false,
            products: [],
            inventoryActivity: {},
            productInventories: [],
            refreshInventory: false,
            pagination: {},
            queryParams: { perpage },
        };
    },
    computed: {
        ...mapGetters([
            'client',
            'lang',
            'site',
            'hasPermission',
        ]),
        tableHeaders() {
            const headers = [
                {
                    text: this.$t('forms.labels.name'),
                    value: 'name',
                },
                {
                    text: this.$t('forms.labels.code'),
                    value: 'code',
                    sortable: false,
                },
                {
                    text: this.$t('forms.labels.msds'),
                    value: 'sds',
                    sortable: false,
                },
                {
                    text: this.$t('forms.labels.report'),
                    value: 'report',
                    sortable: false,
                },
                {
                    text: this.$t('forms.labels.procedure'),
                    value: 'procedure',
                    sortable: false,
                },
            ];

            if (!this.isViewFromSite) {
                headers.splice(2, 0, {
                    text: this.$t('associatedProducts.associatedSites'),
                    value: 'associatedSiteAmount',
                    align: 'center',
                });
            } else {
                headers.splice(2, 0, {
                    text: this.$t('inventory.inInventoryActivity'),
                    value: 'inInventoryActivity',
                    sortable: false,
                    filterable: false,
                });
            }

            return headers;
        },
        isViewFromSite() {
            return !isEmpty(this.site);
        },
        associatedProductIds() {
            return this.products.map(({ id }) => id);
        },
        getItemActions() {
            const actions = [];
            if (this.hasPermission('sites.products.edit')) {
                actions.push(
                    {
                        name: this.$t('sitePage.add'),
                        icon: 'add',
                        mode: 'add',
                    },
                );
                actions.push(
                    {
                        name: this.$t('sitePage.delete'),
                        icon: 'delete',
                        mode: 'delete',
                        iconAndTextColor: '#EF4135',
                    },
                );
            }
            return actions;
        },
    },
    watch: {
        lang: {
            immediate: true,
            handler() {
                this.loadProducts({ initialLoad: true, type: 'initial' });
            },
        },
    },
    async created() {
        this.loading = true;
        await this.loadProducts({ initialLoad: true, type: 'initial' });
        this.loading = false;
    },
    methods: {
        async fetchQuery(queryParams) {
            this.queryParams = {
                ...this.queryParams,
                ...queryParams,
                page: 1,
            };
            this.pagination = {};
            this.products = [];
            await this.loadProducts();
        },

        async loadProducts(overWrite = true) {
            if (isEmpty(this.pagination) || (this.pagination?.currentPage !== this.queryParams?.page)) {
                const { data } = await this.$rq.getSiteProducts(this.site.id, this.queryParams);
                this.pagination = data.pagination;
                const newProducts = data.products.map(product => this.prepareProductForTable(product));
                this.products = overWrite ? newProducts : [...this.products, ...newProducts];
            }
        },

        /**
         * Open the creation form in modal
         */
        onClickAdd() {
            const associatedProductIds = this.isViewFromSite ? this.associatedProductIds : [];

            this.$root.$emit('showModal', {
                title: this.$t('associatedProducts.formTitle'),
                component: Vue.component('AssociateProductForm', AssociateProductForm),
                payload: {
                    clientId: this.client.id,
                    isViewFromSite: this.isViewFromSite,
                    associatedProductIds,
                },
                onConfirm: requestPayload => this.addRequest(requestPayload),
            });
        },
        async removeProduct(product) {
            await this.$rq.deleteSiteProduct(this.site.id, product.id);
            await this.fetchQuery({});
        },
        async addInventoryItem(requestPayload, productItem) {
            const payload = this.$utils.removeEmptyNullOrUndefined(requestPayload, true);
            await this.$rq.addProductInventoryItem(this.site.id, productItem.id, payload);
            this.refreshInventory = true;
            this.$toasted.global.toast_success({
                message: this.$t('inventory.added'),
            });
        },
        onClickAction(item, action) {
            if (action?.mode === 'add') {
                this.$root.$emit('showModal', {
                    title: this.$t('inventory.add'),
                    component: Vue.component('InventoryForm', InventoryForm),
                    onConfirm: requestPayload => {
                        this.refreshInventory = false;
                        this.addInventoryItem(requestPayload, item);
                    },
                });
            } else if (action?.mode === 'delete') {
                this.$root.$emit('showModal', {
                    title: this.$t('products.removeProduct'),
                    component: Vue.component('ConfirmForm', ConfirmForm),
                    payload: {
                        msg: this.$t('serviceProviders.confirmRemove'),
                        productInfo: [item],
                        showBreadcrumbs: true,
                    },
                    onConfirm: () => this.removeProduct(item),
                    isForm: false,
                });
            } else {
                this.onClickAdd();
            }
        },

        /**
         * Send request to API, creating a new association
         *
         * @param {Object} payload
         * @return {Promise}
         */
        async addRequest(requestPayload) {
            await this.$rq.associateSiteProduct(this.site.id, requestPayload);

            this.$toasted.global.toast_success({
                message: this.$t('associatedProducts.successAssociation'),
            });
            await this.fetchQuery({});
        },
        async onChangeInInventoryActivity(value, item) {
            const { code: data, id } = item;
            await this.$rq.updateSiteProduct(this.site.id, id, { inInventoryActivity: value });

            this.$toasted.global.toast_success({
                message: this.$t('msg.productModified', { data }),
            });
        },

        /**
         * Open association product page
         *
         * @param {Object} product
         */
        onClickRedirect(product) {
            this.$router.push({
                name: this.isViewFromSite ? 'siteProduct' : 'clientProduct',
                params: {
                    clientId: this.client.id,
                    siteId: this.site.id,
                    productId: product.id,
                },
            });
        },

        /**
         * @param {Object} file
         * @param {String} lang
         * @return {Object}
         */
        prepareFileForTable(file, lang) {
            return {
                ...file,
                size: prettyBytes(file.size, { locale: this.lang }),
                lang,
            };
        },

        /**
         * @param {Object} product
         * @return {Object}
         */
        prepareProductForTable(product) {
            const {
                associatedSiteAmount, product: id, inInventoryActivity, name, code,
            } = product;
            const result = {
                associatedSiteAmount, id, inInventoryActivity, name, code,
            };
            this.inventoryActivity[result.id] = product.inInventoryActivity;

            FILE_FIELDNAMES.forEach(fileFieldName => {
                result[fileFieldName] = [];
                const fileRef = product[fileFieldName];
                if (isPlainObject(fileRef)) {
                    if (fileRef.id) {
                        result[fileFieldName].push(fileRef);
                    }
                }
            });
            return result;
        },
        async endIntersect(_, __, isIntersecting) {
            const condition = this.pagination.currentPage < this.pagination.totalPageCount;
            if (isIntersecting && condition) {
                this.queryParams.page = this.pagination.currentPage + 1;
                await this.loadProducts(false);
                const ref = this.$refs?.tableRemoteRef?.$refs?.tableRemote;
                if (ref?.onScroll) {
                    ref.onScroll();
                }
            }
        },
    },
};
</script>
<style scoped>
.custom-table .v-data-table__wrapper table tbody tr td:nth-child(2){
    width: 52% !important;
    text-overflow: ellipsis;
}
</style>
